import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function TermsPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Terms of Service
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.terms}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. Introduction & Acceptance
            </h2>
            <p className="text-slate-600 mb-4">
              Welcome to Southwest Florida Vacation Rentals ("SWFL Rentals", "we", "us", or "our"). 
              By accessing or using our website at southwestflvacationrentals.com and related services, 
              you agree to be bound by these Terms of Service.
            </p>
            <p className="text-slate-600 mb-4">
              <strong>SWFL Rentals operates as a marketplace platform</strong> that connects property owners 
              ("Hosts") with travelers ("Guests") seeking vacation rentals in Southwest Florida. 
              We are not a property owner, real estate broker, or travel agent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. Platform Role & Marketplace Clarification
            </h2>
            <p className="text-slate-600 mb-4">
              SWFL Rentals provides a technology platform that enables:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Hosts to list and market their vacation rental properties</li>
              <li>Guests to search, browse, and book vacation rentals</li>
              <li>Communication between Hosts and Guests</li>
              <li>Payment processing facilitation through third-party providers</li>
            </ul>
            <p className="text-slate-600 mb-4">
              <strong>We do not own, manage, or control any properties listed on our platform.</strong> 
              The contractual agreement for any booking is between the Host and Guest directly. 
              We are not a party to any rental agreement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. Account Registration
            </h2>
            <p className="text-slate-600 mb-4">
              To use certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Geographic Scope
            </h2>
            <p className="text-slate-600 mb-4">
              Our platform is exclusively focused on vacation rentals in Southwest Florida, 
              including but not limited to: Cape Coral, Fort Myers, Fort Myers Beach, Sanibel, 
              Captiva, Bonita Springs, Estero, Naples, and Marco Island.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. Prohibited Activities
            </h2>
            <p className="text-slate-600 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Post false, misleading, or fraudulent content</li>
              <li>Circumvent our payment system or booking process</li>
              <li>Harass, threaten, or discriminate against other users</li>
              <li>Use automated systems to access our platform without permission</li>
              <li>Interfere with the proper functioning of our services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Intellectual Property
            </h2>
            <p className="text-slate-600 mb-4">
              All content, trademarks, and intellectual property on our platform are owned by 
              SWFL Rentals or our licensors. You may not copy, modify, distribute, or create 
              derivative works without our express written consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. Limitation of Liability
            </h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law, SWFL Rentals shall not be liable for any 
              indirect, incidental, special, consequential, or punitive damages arising from your 
              use of our platform or any booking made through our services.
            </p>
            <p className="text-slate-600 mb-4">
              <strong>We are not responsible for:</strong>
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>The condition, quality, safety, or legality of listed properties</li>
              <li>The accuracy of listing descriptions or photos</li>
              <li>The ability of Hosts to fulfill bookings</li>
              <li>The conduct of Hosts or Guests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Dispute Resolution
            </h2>
            <p className="text-slate-600 mb-4">
              Any disputes between you and SWFL Rentals shall be resolved through binding 
              arbitration in accordance with the rules of the American Arbitration Association. 
              The arbitration shall take place in Lee County, Florida.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              9. Changes to Terms
            </h2>
            <p className="text-slate-600 mb-4">
              We may modify these Terms at any time. Continued use of our platform after changes 
              constitutes acceptance of the updated Terms. We will provide notice of material 
              changes via email or prominent notice on our website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              10. Contact Information
            </h2>
            <p className="text-slate-600">
              For questions about these Terms, please contact us at:<br />
              Email: legal@southwestflvacationrentals.com<br />
              Address: Southwest Florida Vacation Rentals, FL
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
